import React from "react";
import { Printer, ChefHat, Boxes, ShoppingCart, Building2, PartyPopper, Lightbulb, ShieldAlert, Eye, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Page "Guide opérateur" — pense-bête des 4 flux principaux.
 *
 * Deux modes d'affichage :
 *  - mode normal (dans Layout)            : header + bouton "Aperçu PDF"
 *  - mode preview (route /guide-imprimable, sans Layout) : pleine page A4 + barre d'actions flottante
 *
 * Le bouton "Aperçu PDF" ouvre /guide-imprimable dans un nouvel onglet ;
 * sur cette page, l'utilisateur peut valider visuellement puis cliquer
 * "Imprimer maintenant" qui appelle window.print() (le rendu print est
 * propre grâce au :has(.print-area) géré dans index.css).
 */
export default function GuideOperateur({ preview = false }) {
  const openPreview = () => window.open("/guide-imprimable", "_blank", "noopener,noreferrer");
  const handlePrint = () => window.print();

  if (preview) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] py-6 print:bg-white print:py-0">
        {/* Barre d'actions flottante — masquée à l'impression */}
        <div className="no-print sticky top-0 z-50 bg-white border-b border-[#E5E5E0] shadow-sm">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={() => window.close()}
              data-testid="guide-preview-close"
              title="Ferme cet onglet d'aperçu"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Fermer l&apos;aperçu
            </Button>
            <div className="text-sm text-[#575A57] hidden md:block">
              Aperçu avant impression · Cliquez « Imprimer maintenant » pour ouvrir le dialogue
            </div>
            <Button
              onClick={handlePrint}
              data-testid="guide-preview-print"
              className="bg-[#1F4E3D] hover:bg-[#16382B]"
            >
              <Printer className="w-4 h-4 mr-2" /> Imprimer maintenant
            </Button>
          </div>
        </div>
        {/* Contenu A4 centré, padding généreux pour aérer */}
        <div className="max-w-[210mm] mx-auto bg-white shadow-md p-8 mt-6 print:shadow-none print:p-4 print:mt-0 print:max-w-none">
          <GuideContent printOnlyTitle />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="guide-operateur">
      {/* En-tête + bouton aperçu */}
      <div className="flex items-start justify-between gap-4 flex-wrap no-print">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-[#1F4E3D] flex items-center justify-center shrink-0">
            <ChefHat className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading text-2xl text-[#1A1C1A]">Devenir chef en 4 flux</h2>
            <p className="text-sm text-[#575A57] mt-1">
              Aide-mémoire pour les opérateurs · qui clique où, dans quel ordre, ce qui est auto vs manuel.
            </p>
          </div>
        </div>
        <Button onClick={openPreview} data-testid="guide-preview-btn" className="bg-[#1F4E3D] hover:bg-[#16382B]">
          <Eye className="w-4 h-4 mr-2" /> Aperçu PDF
        </Button>
      </div>

      <GuideContent />
    </div>
  );
}

/* ---------- Contenu réutilisé par les deux modes ---------- */

function GuideContent({ printOnlyTitle = false }) {
  return (
    <div className="space-y-8">
      {/* Titre print-only (ou toujours visible en mode preview) */}
      <div className={`${printOnlyTitle ? "block" : "hidden print:block"} text-center mb-2`}>
        <h1 className="text-2xl font-bold text-[#1A1C1A]">Maison Thaï — Guide opérateur AgroManage</h1>
        <p className="text-xs text-[#575A57] mt-1">4 flux principaux · pense-bête imprimable</p>
      </div>

      {/* Flux 1 — Production sans commande */}
      <FluxSection
        icon={<Boxes className="w-5 h-5" />}
        title="Flux 1 — Production sans commande"
        subtitle="« Je produis pour mon stock, sans client précis. »"
        color="#1F4E3D"
        steps={[
          ["Ordres de Fabrication → + Nouvel OF", "Choisir la recette · Saisir quantité · Date prévue", "Manuel"],
          ["OF brouillon → bouton Planifier", "Décrémente les ingrédients du stock (BOM)", "Auto"],
          ["OF planifié → bouton Démarrer production", "Statut passe à EN_COURS", "Manuel"],
          ["OF en cours → bouton Terminer (saisir qty produite + DLC)", "Crée un lot OF-AAMMJJ-NNN au Stock Conthey. Génère étiquettes.", "Auto"],
          ["Stocks & Lots", "Lot visible avec qty, DLC, traçabilité complète", "Auto"],
          ["Étiquettes → choisir le lot → Imprimer / PDF", "Cocher « Polices agrandies » si lecture à travers vitre distributeur", "Manuel"],
        ]}
        warn="Un OF terminé ne peut être annulé que si aucun mouvement OUT n'a eu lieu sur les lots produits."
      />

      {/* Flux 2 — Vente B2C / B2B */}
      <FluxSection
        icon={<ShoppingCart className="w-5 h-5" />}
        title="Flux 2 — Vente Client ou Revendeur (CMD → OF → BL → Facture)"
        subtitle="« Un client ou un revendeur me commande X unités. »"
        color="#8B1A1A"
        sections={[
          {
            title: "A. Saisie de la commande",
            rows: [
              ["Commandes clients → + Nouvelle CMD", "Choisir client/revendeur · Ajouter lignes (produit + qty)"],
              ["Si revendeur", "Prix auto = override OU % remise OU prix B2B selon fiche revendeur"],
              ["Bouton Valider", "CMD passe en CONFIRME. Réservation logique du besoin."],
            ],
          },
          {
            title: "B. Production (si stock insuffisant)",
            rows: [
              ["Depuis la CMD → bouton Générer OF", "Crée un OF lié à la CMD. Quantité = manquant"],
              ["Suivre Flux 1 étapes 2-4", "Lot disponible en Stock Conthey"],
            ],
          },
          {
            title: "C. Livraison",
            rows: [
              ["Depuis la CMD → bouton Préparer BL", "Sélectionner lots à expédier (FEFO recommandé : DLC la + courte d'abord)"],
              ["Bouton Valider BL", "Décrémente stock. Génère PDF. CMD passe LIVREE."],
              ["Bouton Exécuter transfert", "Mouvement physique enregistré (irrévocable côté traçabilité)"],
            ],
          },
          {
            title: "D. Facturation",
            rows: [
              ["Depuis la CMD LIVREE → bouton Générer facture", "Facture PDF avec QR-bill suisse · adresse 4 lignes · TVA"],
              ["Factures", "Suivi paiement · Marquer PAYEE après réception"],
            ],
          },
        ]}
        warn="Un BL ne peut plus être supprimé si transfer_executed = true (traçabilité HACCP)."
      />

      {/* Flux 3 — Vending machine */}
      <FluxSection
        icon={<Building2 className="w-5 h-5" />}
        title="Flux 3 — Cycle complet Vending Machine ⭐"
        subtitle="« Je gère mes distributeurs automatiques de bout en bout. »"
        color="#2D5A87"
        sections={[
          {
            title: "A. Diagnostic — avant de produire",
            rows: [
              ["État distributeurs", "Vue d'ensemble : qty restante par produit · DLC à risque"],
              ["Distributeurs → cliquer une machine", "Détail par slot : produit, qty, vitesse de vente, DLC"],
              ["Identifier ruptures / DLC J-7", "Liste mentale de la fiche de réappro"],
            ],
          },
          {
            title: "B. Production ciblée",
            rows: [
              ["Ordres de Fabrication → + Nouvel OF", "Cibler les produits identifiés en A.3"],
              ["Terminer l'OF (Flux 1 étape 4)", "Lots créés dans Stock Conthey automatiquement"],
            ],
          },
          {
            title: "C. Préparation fiche réapprovisionnement",
            rows: [
              ["État distributeurs → onglet Fiche de réappro", "Liste machine → produit → qty à amener"],
              ["Imprimer la fiche / tablette", "Pour la tournée"],
            ],
          },
          {
            title: "D. Sortie hors-circuit (DLC dépassée en machine)",
            rows: [
              ["Stocks & Lots → filtre DLC « À risque »", "Identifie les barquettes à retirer"],
              ["Bouton Sortie de stock → Mise hors circuit", "Motif HACCP : DLC_EXPIRED · Notes : « Sortie machine X – jj/mm »"],
              ["Lot baisse à 0 ou diminue", "Tracé HACCP préservé"],
            ],
          },
          {
            title: "E. Remplissage machine (transfert Conthey → distributeur)",
            rows: [
              ["Stocks & Lots → Sortie de stock → Transfert", "Badge bleu « Approvisionnement » apparaît auto"],
              ["Combobox lot (recherche par nom/lot/dépôt)", "Choisir distributeur destination + slot"],
              ["Bouton Valider", "Crée mouvement OUT (Conthey) + IN (machine)"],
              ["Scan terrain sur tablette (optionnel)", "Scan EAN13 d'une barquette = enregistrement rapide du slot"],
            ],
          },
          {
            title: "F. Contrôle des ventes (réconciliation)",
            rows: [
              ["Avec Nayax (quand débloqué)", "Polling auto 15 min · réconcilie ventes ↔ stock"],
              ["Sans Nayax (manuel)", "Ventes → + Saisir vente OU import CSV/XLSX nocturne"],
              ["Chaque vente déduit 1 unité du lot en machine", "Auto · trace par lot conservée"],
              ["Tableau de bord", "CA jour/semaine/mois par machine"],
            ],
          },
        ]}
      />

      {/* Flux 4 — Devis traiteur */}
      <FluxSection
        icon={<PartyPopper className="w-5 h-5" />}
        title="Flux 4 — Devis Traiteur (événementiel)"
        subtitle="« Un événement me demande un devis. »"
        color="#8C6B2F"
        steps={[
          ["Devis traiteur → + Nouveau devis", "Client · date événement · nb convives · lignes (recette/produit)", "Manuel"],
          ["Bouton Générer PDF", "PDF format suisse 4 lignes · conditions générales auto", "Auto"],
          ["Si accepté → bouton Convertir en CMD", "Passe en Flux 2 (production puis livraison)", "Manuel"],
        ]}
      />

      {/* Décisions rapides */}
      <section className="bg-[#FBF4E9] border border-[#E8D3A8] rounded-md p-5 print:break-inside-avoid">
        <h3 className="font-heading text-lg text-[#8C6B2F] flex items-center gap-2 mb-3">
          <Lightbulb className="w-5 h-5" /> Décisions rapides « quand utiliser quoi »
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#E8D3A8]">
                <th className="py-2 pr-4 font-semibold text-[#8C6B2F]">Situation</th>
                <th className="py-2 font-semibold text-[#8C6B2F]">Action</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Production planifiée sans client", "OF direct (Flux 1)"],
                ["Commande client/revendeur reçue", "CMD d'abord (Flux 2)"],
                ["Réappro machine", "OF ciblé puis Transfert (Flux 3)"],
                ["Casse / DLC dépassée", "Mise hors circuit (motif HACCP)"],
                ["Retour de barquettes machine → labo", "Transfert Distributeur → Conthey (badge Inter-distributeurs)"],
                ["Vente directe (dégustation, événement)", "Ventes → manuel"],
                ["Tarifs nouveau revendeur", "Revendeurs → fiche → onglet Tarif → PDF + Correspondance « Nouveau tarif »"],
              ].map((row, i) => (
                <tr key={i} className="border-b border-[#E8D3A8]/40 last:border-0">
                  <td className="py-2 pr-4 text-[#1A1C1A]">{row[0]}</td>
                  <td className="py-2 text-[#3A3A3A]">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Tips de chef */}
      <section className="bg-white border border-[#E5E5E0] rounded-md p-5 print:break-inside-avoid">
        <h3 className="font-heading text-lg text-[#1F4E3D] flex items-center gap-2 mb-3">
          <ChefHat className="w-5 h-5" /> Tips de chef
        </h3>
        <ol className="space-y-2 text-sm text-[#1A1C1A] list-decimal list-inside">
          <li><strong>FEFO toujours</strong> (First-Expire-First-Out) : sortir en priorité les lots dont la DLC est la + courte.</li>
          <li><strong>Toggle « Masquer lots vides »</strong> dans Stocks (coché par défaut) : les lots à 0 restent en base pour HACCP mais sortent de la vue.</li>
          <li><strong>Combobox de recherche partout</strong> : tapez n° lot OU nom produit OU dépôt → trouve immédiatement.</li>
          <li><strong>Badges contextuels</strong> du dialog Sortie de stock : bleu = appro, vert = inter-distributeurs, ocre = livraison client.</li>
          <li><strong>Étiquettes vitrine distributeur</strong> : cocher « Polices agrandies » + « Nutri sur étiquette séparée » si format 51×180 (Epson C3500).</li>
          <li><strong>Correspondance revendeur</strong> : bouton « Modèle : Nouveau tarif » pré-remplit la lettre type. Gain de temps massif pour communiquer les grilles.</li>
        </ol>
      </section>

      {/* Garde-fous HACCP */}
      <section className="bg-[#FFF5F5] border border-[#FED7D7] rounded-md p-5 print:break-inside-avoid">
        <h3 className="font-heading text-lg text-[#8B1A1A] flex items-center gap-2 mb-3">
          <ShieldAlert className="w-5 h-5" /> Garde-fous HACCP à connaître
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#FED7D7]">
                <th className="py-2 pr-4 font-semibold text-[#8B1A1A]">Règle</th>
                <th className="py-2 font-semibold text-[#8B1A1A]">Conséquence</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["BL avec transfer_executed = true", "Non supprimable"],
                ["OF avec lots utilisés en aval", "Non supprimable, doit être annulé"],
                ["Annulation OF", "Possible seulement si aucun mouvement OUT depuis ses lots"],
                ["Mise hors circuit", "Motif obligatoire (DLC_EXPIRED, BROKEN, BLOCKED, RETURN_CONTHEY)"],
                ["Lot à qty = 0", "Reste en base, juste caché de la vue active"],
              ].map((row, i) => (
                <tr key={i} className="border-b border-[#FED7D7]/40 last:border-0">
                  <td className="py-2 pr-4 text-[#1A1C1A]">{row[0]}</td>
                  <td className="py-2 text-[#3A3A3A]">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-center text-sm text-[#575A57] italic pt-4 print:pt-2">
        🎩 Voilà chef, vous êtes prêt. Quand on a un doute, on revient à cette feuille.
      </p>
    </div>
  );
}

/* ---------- Composant interne pour les sections de flux ---------- */

function FluxSection({ icon, title, subtitle, color, steps, sections, warn }) {
  return (
    <section className="bg-white border border-[#E5E5E0] rounded-md p-5 print:break-inside-avoid">
      <header className="flex items-start gap-3 mb-4 pb-3 border-b border-[#E5E5E0]">
        <div
          className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 text-white"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>
        <div>
          <h3 className="font-heading text-xl text-[#1A1C1A]">{title}</h3>
          <p className="text-sm text-[#575A57] italic mt-0.5">{subtitle}</p>
        </div>
      </header>

      {steps && <StepsTable rows={steps} hasAuto />}

      {sections &&
        sections.map((s, i) => (
          <div key={i} className="mt-4 first:mt-0">
            <h4 className="font-semibold text-sm text-[#1F4E3D] uppercase tracking-wider mb-2">
              {s.title}
            </h4>
            <StepsTable rows={s.rows} />
          </div>
        ))}

      {warn && (
        <div className="mt-4 bg-[#FFF5F5] border-l-4 border-[#8B1A1A] px-3 py-2 rounded">
          <p className="text-sm text-[#8B1A1A]">
            <strong>🚨 Important :</strong> {warn}
          </p>
        </div>
      )}
    </section>
  );
}

function StepsTable({ rows, hasAuto = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[#E5E5E0]">
            <th className="py-2 pr-4 font-semibold text-[#575A57] uppercase tracking-wider text-[11px] w-[42%]">
              Où / Action
            </th>
            <th className="py-2 pr-4 font-semibold text-[#575A57] uppercase tracking-wider text-[11px]">
              Ce qui se passe
            </th>
            {hasAuto && (
              <th className="py-2 font-semibold text-[#575A57] uppercase tracking-wider text-[11px] text-right w-[80px]">
                Mode
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[#E5E5E0]/40 last:border-0">
              <td className="py-2 pr-4 font-medium text-[#1A1C1A] align-top">{row[0]}</td>
              <td className="py-2 pr-4 text-[#3A3A3A] align-top">{row[1]}</td>
              {hasAuto && (
                <td className="py-2 text-right align-top">
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                      row[2] === "Auto"
                        ? "bg-[#E8F0E5] text-[#1F4E3D] border-[#C8D9C0]"
                        : "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]"
                    }`}
                  >
                    {row[2]}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
